import some from 'lodash/some';
import uPlot from 'uplot';

import { VIZ_CATEGORICAL } from '@splunk/visualization-color-palettes';
import { LocaleInfo, formatTimestamp } from '../utilities/formatTimestamp';
import { VizTheme, FONT_FAMILY } from '../theme';
import {
    AxisRange,
    GenericDataSet,
    DataSet,
    ScaleConfig,
    SeriesConfig,
    TimestampFormat,
    TickInterval,
    LineDashStyle,
    StepPosition,
} from '../types';
import {
    DAY,
    applyOpacityToColor,
    exists,
    formatValueWithScaleConfig,
    getMergedSeriesConfig,
    getMergedSeriesConfigForField,
    getTickInterval,
    X_AXIS_TIME_OPTIONS,
    SECOND_TO_MILLISECOND,
    getDefaultExpandedFormatTimeStampLabelParts,
} from '../utilities/utils';
import { SeriesInfo } from './types';

const { spline } = uPlot.paths;
const splinePaths = spline ? spline() : undefined;
const STEP_POSITION_ALIGN: Record<'before' | 'after', -1 | 1> = {
    before: -1,
    after: 1,
};

// Default area fill opacity matching Highcharts area chart behavior.
// Highcharts default areaOpacity is 0.75, and canvas rendering squares it (0.75^2 = 0.5625)
// to match the visual output of jsCharting's areaFillOpacity implementation.
const DEFAULT_AREA_FILL_OPACITY = 0.75 ** 2;

/**
 * Converts a LineDashStyle string to a canvas setLineDash array.
 * Based on the specification and reference images provided.
 * @param dashStyle - The dash style string
 * @returns Array of numbers for canvas setLineDash, or undefined for solid
 */
export const getLineDashArray = (dashStyle?: LineDashStyle): number[] | undefined => {
    if (!dashStyle || dashStyle === 'solid') {
        return undefined; // Solid line (no dashes)
    }

    switch (dashStyle) {
        case 'shortDash':
            return [6, 2];
        case 'shortDot':
            return [2, 2];
        case 'shortDashDot':
            return [6, 2, 2, 2];
        case 'shortDashDotDot':
            return [6, 2, 2, 2, 2, 2];
        case 'dot':
            return [2, 6];
        case 'dash':
            return [8, 4];
        case 'longDash':
            return [16, 4];
        case 'dashDot':
            return [8, 4, 2, 4];
        case 'longDashDot':
            return [16, 4, 2, 4];
        case 'longDashDotDot':
            return [16, 4, 2, 4, 2, 4];
        default:
            return undefined;
    }
};

export type SeriesWithKey = uPlot.Series & { key: string };

export type CustomRotatedLabel = { split: number; label: string };
let rotatedLabelsCache: CustomRotatedLabel[] = [];
export const getRotatedLabels = (): CustomRotatedLabel[] => rotatedLabelsCache;
export const setRotatedLabels = (labels: CustomRotatedLabel[]): void => {
    rotatedLabelsCache = labels;
};

/**
 * Calculates the axis size needed for rotated labels
 * Only supports 45 and 90 degree rotations (positive or negative)
 * @param absRotation - Absolute value of rotation angle (45 or 90)
 * @param timeSeriesExpanded - Whether using expanded timestamp format
 * @param hasAxisLabel - Whether an axis label is present
 * @returns The calculated size for the axis
 */
const calculateRotatedLabelSize = (
    absRotation: number,
    timeSeriesExpanded: boolean,
    hasAxisLabel: boolean,
    hideLabels: boolean
): number => {
    // If labels are hidden, no space needed for labels, only title (if present)
    if (hideLabels) {
        return hasAxisLabel ? 25 : 0;
    }

    // Allocate space for title or minimum padding to prevent label cutoff
    let size = 8;

    if (absRotation === 90) {
        size += timeSeriesExpanded ? 57 : 50;
    } else if (absRotation === 45) {
        size += timeSeriesExpanded ? 55 : 50;
    }

    return size;
};

/**
 * Calculates the labelGap (space between labels and axis title) for rotated labels
 * Only supports 45 and 90 degree rotations (positive or negative)
 * @returns The calculated labelGap
 */
const calculateRotatedLabelGap = (absRotation: number): number => {
    if (absRotation === 90) {
        return 4;
    }
    return 12;
};

/**
 * Calculates the gap (space between ticks and labels) for rotated labels
 * @param absRotation - Absolute value of rotation angle
 * @returns The calculated gap
 */
const calculateRotatedTickGap = (absRotation: number): number => {
    // Increased gap for 90-degree rotation to prevent overlap with tick marks
    if (absRotation === 90) {
        return 12;
    }
    // Keep default gap for other rotations (45 degrees, etc.)
    return 6;
};

/**
 * Gets the alignment value for rotated labels
 * uPlot align: 1 = left, 2 = right, undefined = center
 * @param labelRotation - The rotation angle (positive or negative)
 * @returns The alignment value for uPlot
 */
const getRotationAlignment = (labelRotation: number): number => {
    // For positive rotation (clockwise), align left so labels rotate from their start
    // For negative rotation (counter-clockwise), align right so labels rotate from their end
    return labelRotation > 0 ? 1 : 2;
};

const MIDNIGHT_TIMESTAMP = '00:00';
// Set of default colors to use
const defaultSeriesColors = VIZ_CATEGORICAL;

// Render path types
const { bars } = uPlot.paths;
const bars90PercentGap100MaxWidthPaths = bars ? bars({ size: [0.9, 100] }) : undefined;

const DEFAULT_MARKERPOINT_SIZE = 6;

/** Canvas hover marker (SCP-82958 / Figma): outer halo 20px @ 20% series color, white ring 11px, inner dot 9px. */
export const HOVER_MARKER_OUTER_RADIUS_PX = 10;
const HOVER_MARKER_MIDDLE_RADIUS_PX = 5.5;
const HOVER_MARKER_INNER_RADIUS_PX = 4.5;
const HOVER_MARKER_HALO_OPACITY = 0.2;
const HOVER_MARKER_RING_WHITE = '#FFFFFF';

/**
 * Draws the canvas hover marker (line/area): halo, white ring, solid inner dot.
 * Draw order: outer (largest) to inner so the ring reads correctly.
 */
export const drawHoverMarker = (
    ctx: CanvasRenderingContext2D,
    xPos: number,
    yPos: number,
    seriesColor: string
): void => {
    // Use globalAlpha for the halo so any valid canvas fillStyle (hex, rgb, named CSS colors)
    // gets 20% opacity; applyOpacityToColor only understands hex/rgb(a) and would leave named colors opaque.
    ctx.save();
    ctx.globalAlpha = HOVER_MARKER_HALO_OPACITY;
    ctx.beginPath();
    ctx.arc(xPos, yPos, HOVER_MARKER_OUTER_RADIUS_PX, 0, 2 * Math.PI);
    ctx.fillStyle = seriesColor;
    ctx.fill();
    ctx.restore();

    ctx.beginPath();
    ctx.arc(xPos, yPos, HOVER_MARKER_MIDDLE_RADIUS_PX, 0, 2 * Math.PI);
    ctx.fillStyle = HOVER_MARKER_RING_WHITE;
    ctx.fill();

    ctx.beginPath();
    ctx.arc(xPos, yPos, HOVER_MARKER_INNER_RADIUS_PX, 0, 2 * Math.PI);
    ctx.fillStyle = seriesColor;
    ctx.fill();
};

/**
 * Parses a font string and scales it by the device pixel ratio for canvas rendering.
 * This function extracts the font size and font family from a CSS font string,
 * then scales the font size by the provided pixel ratio to ensure proper rendering
 * on high-DPI displays when drawing directly on canvas.
 *
 * @param fontString - Font string in CSS format (e.g., "10px Arial" or "12px Helvetica Neue")
 * @param pixelRatio - Device pixel ratio to scale the font size by (typically window.devicePixelRatio)
 * @returns Scaled font string in CSS format (e.g., "20px Arial" when pixelRatio is 2)
 *
 * @example
 * parseAndScaleFont('10px Arial', 1) // returns '10px Arial'
 * parseAndScaleFont('10px Arial', 2) // returns '20px Arial' (for Retina displays)
 * parseAndScaleFont('invalid', 1)    // returns '10px [FONT_FAMILY]' (fallback)
 */
export const parseAndScaleFont = (fontString: string, pixelRatio: number): string => {
    const pxIndex = fontString.indexOf('px');
    const baseFontSize = pxIndex > 0 ? Number.parseInt(fontString.substring(0, pxIndex), 10) : 10;
    const fontFamily = pxIndex > 0 ? fontString.substring(pxIndex + 2).trim() : FONT_FAMILY;
    return `${baseFontSize * pixelRatio}px ${fontFamily}`;
};

interface GenerateSeriesOptions {
    field: string;
    seriesIdx: number;
    config?: SeriesConfig[keyof SeriesConfig];
    show?: boolean;
    seriesColors?: string[];
    seriesColorsByField?: Record<string, string>;
    markerFill?: string;
    hasSingleDatapoint?: boolean;
    showLineSmoothing?: boolean;
    lineDashStyle?: LineDashStyle;
    stepPosition?: StepPosition;
    areaOpacity?: number;
}

interface GetPointsConfigOptions {
    color: string;
    markerFill?: string;
    hasSingleDatapoint?: boolean;
    markerDisplay?: 'off' | 'filled' | 'outlined';
    hidePoints?: boolean;
}

const getSeriesPointsConfig = ({
    color,
    markerFill,
    hasSingleDatapoint,
    markerDisplay,
    hidePoints,
}: GetPointsConfigOptions) => {
    // For single datapoint series, automatically show filled markers
    if (hasSingleDatapoint) {
        return {
            show: true,
            size: DEFAULT_MARKERPOINT_SIZE,
            fill: color,
        };
    }

    // markerDisplay takes precedence if explicitly set
    if (markerDisplay !== undefined) {
        switch (markerDisplay) {
            case 'off':
                return { show: false };
            case 'filled':
                return {
                    show: true,
                    size: DEFAULT_MARKERPOINT_SIZE,
                    fill: color,
                };
            case 'outlined':
                return {
                    show: true,
                    size: DEFAULT_MARKERPOINT_SIZE,
                    fill: markerFill || '#FFFFFF',
                    stroke: color,
                    width: 1,
                };
            default:
                return { show: false };
        }
    }

    // Fall back to hidePoints for backward compatibility
    if (hidePoints !== undefined) {
        return { show: !hidePoints };
    }

    // Default to 'off' (markers hidden)
    return { show: false };
};

/**
 * @param options.field - Field name
 * @param options.seriesIdx - Series index, starts at "1"
 * @param options.config - Series configuration
 * @param options.show - Whether or not the series will be shown
 * @returns Series configuration for uPlot
 */
export const generateSeries = ({
    field,
    seriesIdx,
    config,
    show,
    seriesColors,
    seriesColorsByField,
    markerFill,
    hasSingleDatapoint,
    showLineSmoothing = false,
    lineDashStyle,
    stepPosition,
    areaOpacity,
}: GenerateSeriesOptions): SeriesWithOptionalStackingGroup => {
    if (seriesIdx === 0) {
        return {
            label: '_time',
            key: '_time',
        };
    }

    let seriesColorsInput = defaultSeriesColors;
    if (seriesColors?.length) {
        seriesColorsInput = seriesColors;
    }

    if (!config) {
        return {
            key: field,
            label: field,
            spanGaps: true,
            stroke: seriesColorsInput[(seriesIdx - 1) % seriesColorsInput.length],
        };
    }

    const label = config.label || field;
    const seriesColorsByFieldColor = seriesColorsByField?.[field];
    const color =
        config.color ||
        seriesColorsByFieldColor ||
        seriesColorsInput[(seriesIdx - 1) % seriesColorsInput.length];
    const overrides = config.overrides || {};

    // Get dash array from lineDashStyle (config takes precedence over passed parameter)
    const dashArray = getLineDashArray(config.lineDashStyle || lineDashStyle);
    // Use stepped interpolation when configured (config takes precedence over passed parameter)
    const resolvedStepPosition = config.stepPosition ?? stepPosition;
    const stepAlign =
        resolvedStepPosition === 'before' || resolvedStepPosition === 'after'
            ? STEP_POSITION_ALIGN[resolvedStepPosition]
            : undefined;
    const stepPaths =
        stepAlign !== undefined && uPlot.paths.stepped
            ? uPlot.paths.stepped({ align: stepAlign })
            : undefined;
    const pointsConfig = getSeriesPointsConfig({
        color,
        markerFill,
        hasSingleDatapoint,
        markerDisplay: config.markerDisplay,
        hidePoints: config.hidePoints,
    });
    switch (config.type) {
        case 'area': {
            return {
                show,
                key: field,
                label,
                stroke: color,
                fill: applyOpacityToColor(color, areaOpacity ?? DEFAULT_AREA_FILL_OPACITY),
                scale: config.scale,
                spanGaps: true,
                points: pointsConfig,
                paths: stepPaths || (showLineSmoothing ? splinePaths : undefined),
                dash: dashArray,
                ...overrides,
            };
        }
        case 'points':
            return {
                show,
                key: field,
                paths: (): null => null,
                points: config.pointSize
                    ? {
                          space: 0,
                          size: config.pointSize,
                          fill: color,
                      }
                    : { space: 0 },
                label,
                stroke: color,
                fill: color,
                scale: config.scale,
                spanGaps: false,
                ...overrides,
            };
        case 'bar':
            return {
                show,
                key: field,
                label,
                stroke: color,
                fill: color,
                scale: config.scale,
                spanGaps: true,
                paths: bars90PercentGap100MaxWidthPaths,
                points: pointsConfig,
                ...overrides,
            };
        case 'stacked-bar':
            return {
                show,
                key: field,
                label,
                stroke: color,
                fill: color,
                scale: config.scale,
                spanGaps: true,
                paths: bars90PercentGap100MaxWidthPaths,
                negY: false,
                points: pointsConfig,
                stacking: {
                    mode: 'normal',
                    group: 'A',
                },
                ...overrides,
            };
        case 'stacked-area':
            return {
                show,
                key: field,
                label,
                stroke: color,
                fill: applyOpacityToColor(color, areaOpacity ?? DEFAULT_AREA_FILL_OPACITY),
                scale: config.scale,
                spanGaps: true,
                negY: false,
                points: pointsConfig,
                paths: stepPaths || (showLineSmoothing ? splinePaths : undefined),
                dash: dashArray,
                stacking: {
                    mode: 'normal',
                    group: 'A',
                },
                ...overrides,
            };
        case 'line':
        default:
            return {
                show,
                key: field,
                label,
                stroke: color,
                fill: undefined,
                scale: config.scale,
                spanGaps: true,
                points: pointsConfig,
                paths: stepPaths || (showLineSmoothing ? splinePaths : undefined),
                dash: dashArray,
                ...overrides,
            };
    }
};

// Plot types that should have an expanded range
const PLOT_TYPES_TO_EXPAND_RANGE: SeriesConfig[string]['type'][] = ['bar', 'stacked-bar'];

export interface MapDSToSeriesOptions {
    fields: GenericDataSet['fields'];
    hiddenSeries?: string[];
    seriesColors?: string[];
    seriesColorsByField?: Record<string, string>;
    markerFill?: string;
    dataLength?: number;
    seriesConfig?: SeriesConfig;
    scaleConfig?: ScaleConfig;
    showLineSmoothing?: boolean;
    stepPosition?: StepPosition;
    areaOpacity?: number;
}

export const mapDSToSeries = ({
    fields,
    hiddenSeries,
    seriesColors,
    seriesColorsByField,
    markerFill,
    dataLength,
    seriesConfig = {},
    scaleConfig = {},
    showLineSmoothing = false,
    stepPosition,
    areaOpacity,
}: MapDSToSeriesOptions): SeriesWithKey[] => {
    const hasSingleDatapoint = dataLength === 1;
    return fields.map((field, index) => {
        const mergedSeriesConfig = getMergedSeriesConfigForField(field, seriesConfig, scaleConfig);
        const showSeries = !hiddenSeries?.includes(field);
        // Extract lineDashStyle from the merged series config
        const lineDashStyle = mergedSeriesConfig?.lineDashStyle;
        return generateSeries({
            field,
            seriesIdx: index,
            config: mergedSeriesConfig,
            show: showSeries,
            seriesColors,
            seriesColorsByField,
            markerFill,
            hasSingleDatapoint,
            showLineSmoothing,
            lineDashStyle,
            stepPosition,
            areaOpacity,
        });
    });
};

export const getMaxRangeValue = (numberOfDatapoints: number, max: number, range?: AxisRange): number => {
    if (range) {
        // Incorrect range is applied when there is only 1 datapoint
        return numberOfDatapoints < 2 ? range.max : Math.max(max, range.max);
    }
    return max;
};

export const isTimeSeriesData = (dataSet: DataSet | GenericDataSet): boolean =>
    dataSet && dataSet.fields[0] && dataSet.fields[0] === '_time';

export const mapDSToScales = (opts: {
    seriesConfig?: SeriesConfig;
    scaleConfig?: ScaleConfig;
    range?: AxisRange;
    isTimeSeries?: boolean;
    yAxisMajorTickInterval?: number;
    y2AxisMajorTickInterval?: number;
    showYAxisWithZero?: boolean;
    showY2AxisWithZero?: boolean;
}): uPlot.Scales => {
    const {
        seriesConfig = {},
        scaleConfig = {},
        range,
        isTimeSeries = true,
        yAxisMajorTickInterval,
        y2AxisMajorTickInterval,
        showYAxisWithZero,
        showY2AxisWithZero,
    } = opts;
    const mergedSeriesConfig = getMergedSeriesConfig(seriesConfig, scaleConfig);

    // Define the default range function for x scale
    const xScaleRangeFunction = (u: uPlot, min: number, max: number): [number, number] => {
        if (isTimeSeries === false) {
            // For categorical data with single datapoint, add padding to center it
            if (u.data[0].length === 1) {
                return [-0.5, 0.5]; // Center the single datapoint at position 0
            }
            // For categorical data, range goes from 0 to number of datapoints
            return [0, u.data[0].length];
        }

        // If we have a bar-like plot, expand range to include room to show full bar
        const shouldExpandRange = some(
            mergedSeriesConfig,
            v => exists(v.type) && PLOT_TYPES_TO_EXPAND_RANGE.includes(v.type)
        );

        // Use range if provided
        const minValue = range ? Math.min(min, range.min) : min;
        const maxValue = getMaxRangeValue(u.data[0].length, max, range);

        // For single datapoint, add padding to create a visible range
        // and ensure the datapoint appears centered
        if (u.data[0].length === 1) {
            const padding = DAY; // Use one day total range
            const center = minValue; // The single datapoint timestamp
            return [center - padding / 2, center + padding / 2];
        }

        if (shouldExpandRange && u.data[0].length) {
            const dailyBarWidth = !!range && range.dailyResolution;
            const width = maxValue - minValue;
            const barWidth = dailyBarWidth ? DAY : width / u.data[0].length;
            const offset = barWidth / 2;
            return [minValue - offset, maxValue + offset];
        }

        return [minValue, maxValue];
    };

    // Helper to create range function that aligns to interval boundaries
    const createIntervalAlignedRange = (interval: number): uPlot.Scale.Range => {
        return (u: uPlot, dataMin: number, dataMax: number): [number, number] => {
            // uPlot already applies ~10% padding, so dataMin/dataMax are padded values
            // We just need to align them to interval boundaries
            let alignedMin = Math.floor(dataMin / interval) * interval;
            const alignedMax = Math.ceil(dataMax / interval) * interval;

            // Prevent negative axis minimum when all data is positive
            // If dataMax is positive and alignedMin ended up negative due to padding/flooring,
            // align to 0 or the first positive interval boundary instead
            if (dataMax > 0 && alignedMin < 0) {
                alignedMin = 0;
            }

            return [alignedMin, alignedMax];
        };
    };

    // Range function that forces zero to be included (for showYAxisWithZero: true)
    const forceZeroRange = (u: uPlot, dataMin: number, dataMax: number): [number, number] => {
        // For all-positive data (dataMin >= 0): force min to 0, add padding to max
        if (dataMin >= 0) {
            const padding = dataMax * 0.1;
            return [0, dataMax + padding];
        }
        // For all-negative data (dataMax <= 0): add padding to min, force max to 0
        if (dataMax <= 0) {
            const padding = Math.abs(dataMin) * 0.1;
            return [dataMin - padding, 0];
        }
        // For mixed data (crosses zero): use uPlot's rangeNum with padding on both sides
        return uPlot.rangeNum(Math.min(0, dataMin), Math.max(0, dataMax), 0.1, false);
    };

    // Determine the default Y-axis range function
    const getYAxisRange = (includeZero: boolean | undefined, tickInterval?: number) => {
        // Priority: tickInterval > includeZero > auto-range
        if (tickInterval && tickInterval > 0) {
            return createIntervalAlignedRange(tickInterval);
        }
        if (includeZero === true) {
            return forceZeroRange;
        }
        return undefined;
    };

    // Build scales object starting with x scale
    const scales: uPlot.Scales = {
        x: {
            time: !!isTimeSeries,
            range: xScaleRangeFunction,
        },
    };

    // Process custom Y scales from scaleConfig
    Object.keys(scaleConfig)
        .filter(key => key !== 'x')
        .forEach(key => {
            const isRightSide = scaleConfig[key].side === 'right';
            const tickInterval = isRightSide ? y2AxisMajorTickInterval : yAxisMajorTickInterval;
            const includeZero = isRightSide ? showY2AxisWithZero : showYAxisWithZero;

            // Priority: explicit range > interval alignment > include zero > auto-range
            let rangeFunction;
            if (scaleConfig[key].range) {
                rangeFunction = scaleConfig[key].range;
            } else {
                rangeFunction = getYAxisRange(includeZero, tickInterval);
            }

            scales[key] = {
                distr: scaleConfig[key].type === 'log' ? 3 : 1,
                range: rangeFunction,
            };
        });

    // If no 'y' scale was defined, create a default one with includeZero logic
    if (!scales.y) {
        const rangeFunction = getYAxisRange(showYAxisWithZero, yAxisMajorTickInterval);
        if (rangeFunction) {
            scales.y = {
                range: rangeFunction,
            };
        }
    }

    return scales;
};

export const isShowOnlyRangeValues = (range: AxisRange): boolean => {
    return !!range && !!range.dailyResolution;
};

export const isTimeSeriesWithExpandedTimeFormat = (
    isTimeSeries: boolean,
    timestampFormat: TimestampFormat
): boolean => {
    return isTimeSeries && timestampFormat === 'expanded';
};

export const isXScaleConfig = (scaleConfigInput: ScaleConfig): boolean => {
    return !!scaleConfigInput && Object.keys(scaleConfigInput).length > 0 && !!scaleConfigInput.x;
};

export const lastTickHideCondition = (
    splits: number[],
    idx: number,
    isTimeSeries: boolean,
    timestampFormat: TimestampFormat
) => {
    // Only hide last tick for timeseries data
    return splits.length > 1 && idx === splits.length - 1 && isTimeSeries && timestampFormat === 'expanded';
};

/**
 * Checks whether there is enough horizontal space to render the last tick label
 * for an expanded timeseries format without it overflowing the right edge of the plot area.
 *
 * Expanded labels are left-aligned to the tick position, so we check whether
 * tickX + fullLabelWidth exceeds the plot right edge.
 *
 * @param u - The uPlot instance (provides bbox and valToPos, both in physical px)
 * @param lastSplit - The value of the last tick
 * @param label - The label text (may be multiline, separated by '\n')
 * @returns true if there is enough space to render the label without clipping
 */
export const hasSpaceForLastTick = (u: uPlot, lastSplit: number, label: string): boolean => {
    if (!u?.bbox || !u?.ctx) {
        return false;
    }
    const tickX = u.valToPos(lastSplit, 'x', true); // physical px

    // Set the axis font before measuring so measureText uses the same DPR-scaled
    // font that uPlot uses during the draw phase. uPlot's values() callback runs
    // during layout before draw-time font styling is applied, so ctx.font may be
    // stale. uPlot stores the x-axis font as [scaledFontString, fontSize, fontSizeCss].
    const prevFont = u.ctx.font;
    const xAxisFont = u.axes?.[0]?.font;
    if (xAxisFont) {
        // eslint-disable-next-line no-param-reassign
        u.ctx.font = Array.isArray(xAxisFont) ? xAxisFont[0] : xAxisFont;
    }

    // Measure the widest line of the (potentially multiline) label
    const lines = label.split('\n');
    const labelWidth = lines.reduce((max, line) => Math.max(max, u.ctx.measureText(line).width), 0);

    // Restore the previous font
    // eslint-disable-next-line no-param-reassign
    u.ctx.font = prevFont;

    // The canvas lives inside .u-under which has overflow:hidden and whose width
    // in physical px equals bbox.left + bbox.width (bboxRight). Labels drawn beyond
    // bboxRight get clipped by the .u-under container. Since expanded-format labels
    // are left-aligned to the tick position, hide the last label if it would overflow.
    const bboxRight = u.bbox.left + u.bbox.width;

    return tickX + labelWidth <= bboxRight;
};

/**
 * Generates an expanded timestamp label and updates the last seen day, month, and year.
 * This function is intended to be called iteratively for time series data.
 *
 * @param {number} millisecondsEpoch - The timestamp in milliseconds since epoch.
 * @param {TickInterval} tickInterval - The interval of the tick (e.g., '1d', '7d', '1mo').
 * @param {string} locale - The locale string for date formatting.
 * @param {number} idx - The index of the current label in a sequence (e.g., 0 for the first label).
 * @param {string} currentLastDay - The 'day' string from the previously processed timestamp.
 * @param {string} currentLastMonth - The 'month' string from the previously processed timestamp.
 * @param {string} currentLastYear - The 'year' string from the previously processed timestamp.
 * @returns {{label: string, lastDay: string, lastMonth: string, lastYear: string}} An object
 *          containing the formatted label string and the updated last day, month, and year values.
 */
export const getExpandedTimestampLabelAndState = (opts: {
    millisecondsEpoch: number;
    tickInterval: TickInterval;
    locale: LocaleInfo;
    idx: number;
    currentLastDay: string;
    currentLastMonth: string;
    currentLastYear: string;
    xAxisMaxLabelParts: number;
}): { label: string; lastDay: string; lastMonth: string; lastYear: string } => {
    const {
        millisecondsEpoch,
        tickInterval,
        locale,
        idx,
        currentLastDay,
        currentLastMonth,
        currentLastYear,
        xAxisMaxLabelParts,
    } = opts;
    const { time, day, month, year } = getDefaultExpandedFormatTimeStampLabelParts(
        millisecondsEpoch,
        tickInterval,
        locale
    );

    const labelParts = [];
    let newLastDay = currentLastDay; // Initialize with the values passed in
    let newLastMonth = currentLastMonth;
    let newLastYear = currentLastYear;

    // label formatting based on the tickInterval
    if (tickInterval !== '1d' && tickInterval !== '7d' && tickInterval !== '1mo') {
        labelParts.push(time);
    }

    // Condition for adding day
    if (tickInterval !== '1mo' && (idx === 0 || (day !== newLastDay && !newLastDay.includes(day)))) {
        labelParts.push(day);
        newLastDay = day; // Update the local variable for the return value

        // pop the first one if not enough space since we prioritize year > month > day > time
        if (xAxisMaxLabelParts < labelParts.length) {
            labelParts.shift();
        }
    }

    // Condition for adding month
    if (tickInterval === '1mo' && month !== newLastMonth) {
        labelParts.push(month);
        newLastMonth = month; // Update the local variable for the return value
    }

    // Condition for adding year
    if (idx === 0 || year !== newLastYear) {
        labelParts.push(year);
        newLastYear = year; // Update the local variable for the return value

        // pop the first one if not enough space since we prioritize year > month > day > time
        if (xAxisMaxLabelParts < labelParts.length) {
            labelParts.shift();
        }
    }

    return {
        label: labelParts.join('\n'),
        lastDay: newLastDay,
        lastMonth: newLastMonth,
        lastYear: newLastYear,
    };
};

/**
 * Draws minor grid lines between major ticks on the Y or Y2 axis
 * @param u - uPlot instance
 * @param scaleKey - Scale key ('y' or 'y2')
 * @param strokeColor - Color of the minor grid lines
 * @param showMajorGridLines - Whether major grid lines are shown (if false, draws minor lines at major positions too)
 * @param minorTickCount - Number of minor ticks between major ticks (default 4)
 */
/** Helper to check if a Y position is within the plot area */
const isWithinPlotArea = (y: number, bbox: { top: number; height: number }): boolean =>
    y >= bbox.top && y <= bbox.top + bbox.height;

/** Helper to draw a horizontal line at a given Y position */
const drawHorizontalLine = (
    ctx: CanvasRenderingContext2D,
    y: number,
    xLeft: number,
    xRight: number,
    bbox: { top: number; height: number }
): void => {
    if (isWithinPlotArea(y, bbox)) {
        ctx.moveTo(xLeft, y);
        ctx.lineTo(xRight, y);
    }
};

/** Helper to get axis splits for minor grid lines */
const getAxisSplits = (u: uPlot, scaleKey: string): number[] | null => {
    const axis = u.axes.find(a => a.scale === scaleKey || (!a.scale && scaleKey === 'y'));
    if (!axis) return null;

    // @ts-expect-error - accessing internal uPlot property
    // eslint-disable-next-line no-underscore-dangle
    const splits: number[] = axis._splits;
    return splits?.length >= 2 ? splits : null;
};

export const drawMinorGridLines = (
    u: uPlot,
    scaleKey: string,
    strokeColor: string,
    showMajorGridLines: boolean,
    minorTickCount = 4
): void => {
    const { ctx, bbox } = u;
    const scale = u.scales[scaleKey];

    if (scale?.min == null || scale?.max == null) {
        return;
    }

    const splits = getAxisSplits(u, scaleKey);
    if (!splits) {
        return;
    }

    ctx.save();
    ctx.beginPath();
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = 1;
    ctx.setLineDash([2, 2]); // Dashed line for minor grid

    const xLeft = bbox.left;
    const xRight = bbox.left + bbox.width;

    // Calculate loop bounds based on whether major grid lines are shown
    const startJ = showMajorGridLines ? 1 : 0;
    const endJ = showMajorGridLines ? minorTickCount : minorTickCount + 1;

    // Draw minor grid lines between each pair of major ticks
    for (let i = 0; i < splits.length - 1; i += 1) {
        const step = (splits[i + 1] - splits[i]) / (minorTickCount + 1);

        for (let j = startJ; j <= endJ; j += 1) {
            const minorVal = splits[i] + step * j;
            const y = Math.round(u.valToPos(minorVal, scaleKey, true));
            drawHorizontalLine(ctx, y, xLeft, xRight, bbox);
        }
    }

    // If major grid lines are off, draw at the last major tick position too
    if (!showMajorGridLines) {
        const lastSplit = splits.at(-1);
        const y = Math.round(u.valToPos(lastSplit, scaleKey, true));
        drawHorizontalLine(ctx, y, xLeft, xRight, bbox);
    }

    ctx.stroke();
    ctx.restore();
};

/**
 * Draws major tick marks on the Y or Y2 axis
 * @param u - uPlot instance
 * @param scaleKey - Scale key ('y' or 'y2')
 * @param strokeColor - Color of the tick marks
 * @param tickSize - Length of tick marks in pixels (default 6)
 * @param side - 'left' for Y-axis, 'right' for Y2-axis
 */
export const drawMajorTicks = (
    u: uPlot,
    scaleKey: string,
    strokeColor: string,
    tickSize = 6,
    side: 'left' | 'right' = 'left'
): void => {
    const { ctx, bbox, scales } = u;
    const scale = scales[scaleKey];

    if (!ctx || !bbox || scale?.min == null || scale?.max == null) {
        return;
    }

    const splits = getAxisSplits(u, scaleKey);
    if (!splits || splits.length === 0) {
        return;
    }

    // Get device pixel ratio from uPlot (accounts for canvas scaling)
    // @ts-expect-error - accessing internal uPlot property
    // eslint-disable-next-line no-underscore-dangle
    const pxRatio = u._pxRatio ?? window.devicePixelRatio ?? 1;
    const scaledTickSize = tickSize * pxRatio;

    ctx.save();
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = 1 * pxRatio;

    // Draw tick at each major split position
    const yTop = bbox.top;
    const yBottom = bbox.top + bbox.height;
    const xLeft = bbox.left;
    const xRight = bbox.left + bbox.width;

    ctx.beginPath();
    for (const splitVal of splits) {
        const y = Math.round(u.valToPos(splitVal, scaleKey, true));
        // Only draw ticks within the plot area Y bounds
        if (y >= yTop && y <= yBottom) {
            if (side === 'left') {
                // Draw tick extending INTO the plot from left edge
                ctx.moveTo(xLeft, y);
                ctx.lineTo(xLeft + scaledTickSize, y);
            } else {
                // Draw tick extending INTO the plot from right edge
                ctx.moveTo(xRight - scaledTickSize, y);
                ctx.lineTo(xRight, y);
            }
        }
    }
    ctx.stroke();
    ctx.restore();
};

/**
 * Draws minor tick marks on the Y or Y2 axis (between major ticks)
 * @param u - uPlot instance
 * @param scaleKey - Scale key ('y' or 'y2')
 * @param strokeColor - Color of the tick marks
 * @param tickSize - Length of tick marks in pixels (default 4)
 * @param side - 'left' for Y-axis, 'right' for Y2-axis
 */
export const drawMinorTicks = (
    u: uPlot,
    scaleKey: string,
    strokeColor: string,
    tickSize = 4,
    side: 'left' | 'right' = 'left'
): void => {
    const { ctx, bbox, scales } = u;
    const scale = scales[scaleKey];

    if (!ctx || !bbox || scale?.min == null || scale?.max == null) {
        return;
    }

    // Get major splits to determine minor tick positions
    const majorSplits = getAxisSplits(u, scaleKey);
    if (!majorSplits || majorSplits.length < 2) {
        return; // Need at least 2 major ticks to draw minor ticks between them
    }

    // Calculate minor tick positions (between major ticks)
    const minorTicks: number[] = [];
    for (let i = 0; i < majorSplits.length - 1; i += 1) {
        const start = majorSplits[i];
        const end = majorSplits[i + 1];
        const interval = end - start;

        // Place 4 minor ticks between each major tick (dividing into 5 segments)
        const positions = [1, 2, 3, 4];
        positions.forEach(position => {
            minorTicks.push(start + (interval * position) / 5);
        });
    }

    if (minorTicks.length === 0) {
        return;
    }

    // Get device pixel ratio from uPlot (accounts for canvas scaling)
    // @ts-expect-error - accessing internal uPlot property
    // eslint-disable-next-line no-underscore-dangle
    const pxRatio = u._pxRatio ?? window.devicePixelRatio ?? 1;
    const scaledTickSize = tickSize * pxRatio;

    ctx.save();
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = 1 * pxRatio;

    // Draw tick at each minor position
    const yTop = bbox.top;
    const yBottom = bbox.top + bbox.height;
    const xLeft = bbox.left;
    const xRight = bbox.left + bbox.width;

    ctx.beginPath();
    for (const tickVal of minorTicks) {
        const y = Math.round(u.valToPos(tickVal, scaleKey, true));
        // Only draw ticks within the plot area Y bounds
        if (y >= yTop && y <= yBottom) {
            if (side === 'left') {
                // Draw tick extending INTO the plot from left edge
                ctx.moveTo(xLeft, y);
                ctx.lineTo(xLeft + scaledTickSize, y);
            } else {
                // Draw tick extending INTO the plot from right edge
                ctx.moveTo(xRight - scaledTickSize, y);
                ctx.lineTo(xRight, y);
            }
        }
    }
    ctx.stroke();
    ctx.restore();
};

interface MapScalesToAxesParams {
    xAxisResolution: number;
    xAxisMaxLabelParts?: number;
    theme: VizTheme;
    locale: LocaleInfo;
    hideXAxis?: true;
    range?: AxisRange;
    isTimeSeries?: boolean;
    rawXaxisValues?: (string | number | null)[];
    scaleConfig?: ScaleConfig;
    timestampFormat?: TimestampFormat;
    dataLength?: number;
    /** Show major grid lines on the X-axis; default false */
    showXMajorGridLines?: boolean;
    /** Show major grid lines on the Y-axis; default true */
    showYMajorGridLines?: boolean;
    /** Show major grid lines on the Y2-axis (right axis); default false */
    showY2MajorGridLines?: boolean;
    /** Show major tick marks on the X-axis; default true */
    showXMajorTicks?: boolean;
    /** Size (length) of major tick marks on the X-axis in pixels; uses uPlot default if not specified */
    xAxisMajorTickSize?: number;
    /** Major tick interval for the Y-axis; controls spacing between ticks/labels */
    yAxisMajorTickInterval?: number;
    /** Major tick interval for the Y2-axis; controls spacing between ticks/labels */
    y2AxisMajorTickInterval?: number;
    /** Data columns for calculating dynamic Y-axis width */
    dataColumns?: ((string | number | null)[] | number[])[];
    /** Whether this is a Y2-only scenario (Y2 has data but Y doesn't) */
    isY2Only?: boolean;
}

/**
 * Helper function to create a values formatter for y-axis with hideLabels support
 */
const createYAxisValuesFormatter = (
    config: ScaleConfig[string],
    xAxisResolution: number
): ((u: uPlot, splits: number[]) => (string | null)[]) => {
    return (_: uPlot, splits: number[]): (string | null)[] => {
        if (config.hideLabels) {
            return splits.map(() => '');
        }
        const formattedValues = splits.map(v => {
            if (v === null) {
                return null;
            }
            return exists(config) ? formatValueWithScaleConfig(v, config, xAxisResolution) : v.toString();
        });

        // Hide duplicate consecutive labels to prevent overlapping text
        // This can happen when uPlot generates non-integer tick positions that round to the same value
        return formattedValues.map((label, idx) => {
            if (idx > 0 && label === formattedValues[idx - 1]) {
                return null; // Hide duplicate
            }
            return label;
        });
    };
};

/**
 * Helper function to determine the appropriate values formatter for y-axis
 */
const getYAxisValuesConfig = (
    config: ScaleConfig[string],
    xAxisResolution: number
): uPlot.Axis.Values | undefined => {
    if (config.tickFmt) {
        return createYAxisValuesFormatter(config, xAxisResolution);
    }
    if (config.hideLabels) {
        return (): string[] => [];
    }
    return undefined;
};

/**
 * Formats compact timestamp based on range and time conditions
 */
const formatCompactTimestamp = (
    millisecondsEpoch: number,
    xRange: number,
    showOnlyRangeValues: boolean,
    locale: LocaleInfo
): string | null => {
    const defaultLocale = { ...locale, twelveHourTimeFormat: locale.twelveHourTimeFormat ?? true };
    const timestampString = formatTimestamp(millisecondsEpoch, 'timeCompact', defaultLocale);
    const isItMidnight = timestampString === MIDNIGHT_TIMESTAMP;
    const isItMoreThanADay = xRange >= X_AXIS_TIME_OPTIONS['1Day'];
    const isItLessThanADay = xRange < X_AXIS_TIME_OPTIONS['1Day'];

    if (showOnlyRangeValues) {
        return formatTimestamp(millisecondsEpoch, 'dateVerbose', defaultLocale);
    }
    if ((isItMoreThanADay && isItMidnight) || (isItLessThanADay && isItMidnight)) {
        return formatTimestamp(millisecondsEpoch, 'dateCompact', defaultLocale);
    }
    if (isItLessThanADay) {
        return formatTimestamp(millisecondsEpoch, 'timeCompact', defaultLocale);
    }
    return null;
};

/**
 * Creates the splits function for x-axis based on data type and length
 */
const createXAxisSplits = (
    showOnlyRangeValues: boolean,
    range: AxisRange | undefined,
    dataLength: number,
    isTimeSeries: boolean
): uPlot.Axis.Splits | undefined => {
    if (showOnlyRangeValues && range) {
        return [range.min, range.max];
    }
    if (dataLength === 1) {
        return (u: uPlot): number[] => {
            if (isTimeSeries) {
                return u.data[0] && u.data[0].length > 0 ? [u.data[0][0]] : [];
            }
            return [0];
        };
    }
    if (!isTimeSeries && dataLength > 1) {
        return (u: uPlot): number[] => {
            const numPoints = u.data[0].length;
            const ticks: number[] = [];
            for (let i = 0; i <= numPoints; i += 1) {
                ticks.push(i);
            }
            return ticks;
        };
    }
    return undefined;
};

/**
 * Measures the width (in physical/device pixels) of the widest line of the worst-case
 * expanded timestamp label for the given tick interval, locale, and xAxisMaxLabelParts.
 *
 * The first tick (idx=0, no prior state) always produces the maximum label because all
 * parts (time + day + year) are included. A representative epoch is used so the result
 * reflects real locale-specific strings (e.g. long month names).
 *
 * Note: ctx.measureText returns physical pixels. Callers that need CSS pixels must
 * divide the result by window.devicePixelRatio.
 */
export const getWorstCaseExpandedLabelWidth = (
    ctx: CanvasRenderingContext2D,
    tickInterval: TickInterval,
    locale: LocaleInfo,
    xAxisMaxLabelParts: number,
    axisFont: string
): number => {
    // Step size in ms for each tick interval bucket
    const intervalStepMs: Record<TickInterval, number> = {
        '1s': 1000,
        '1m': 60 * 1000,
        '30m': 30 * 60 * 1000,
        '1h': 60 * 60 * 1000,
        '12h': 12 * 60 * 60 * 1000,
        '1d': 24 * 60 * 60 * 1000,
        '7d': 7 * 24 * 60 * 60 * 1000,
        '1mo': 30 * 24 * 60 * 60 * 1000,
    };
    const stepMs = intervalStepMs[tickInterval] ?? 60 * 1000;

    // Number of ticks to sample to find the widest typical label.
    // - sub-day (1s–12h): time labels repeat on a 12/24h cycle; 24 steps covers all hours
    // - 1d/7d: day names repeat on a 7-day cycle; 7 steps covers all day names
    // - 1mo: month names repeat on a 12-month cycle; 12 steps covers all month names
    const sampleCount: Record<TickInterval, number> = {
        '1s': 1,
        '1m': 1,
        '30m': 1,
        '1h': 24,
        '12h': 2,
        '1d': 7,
        '7d': 7,
        '1mo': 12,
    };
    const numSamples = sampleCount[tickInterval] ?? 1;

    // Start one step after a representative epoch so day/year state from tick 0 is set,
    // and subsequent ticks only show the changing part (time, day-of-week, or month name).
    // Use a Monday in a long-named month (Sep 2, 2024 = Monday) as the anchor so
    // we cover the widest day names and month names in the sample window.
    const ANCHOR_EPOCH_MS = 1725235200000; // 2024-09-02 00:00 UTC (Monday, September)

    const anchorResult = getExpandedTimestampLabelAndState({
        millisecondsEpoch: ANCHOR_EPOCH_MS,
        tickInterval,
        locale,
        idx: 0,
        currentLastDay: '',
        currentLastMonth: '',
        currentLastYear: '',
        xAxisMaxLabelParts,
    });

    // Temporarily set the axis label font so measureText uses the correct metrics.
    const prevFont = ctx.font;
    ctx.font = axisFont;

    const measureWidest = (label: string): number =>
        label.split('\n').reduce((max, line) => Math.max(max, ctx.measureText(line).width), 0);

    // Walk through numSamples ticks and find the maximum label width.
    let maxWidth = measureWidest(anchorResult.label);
    let { lastDay } = anchorResult;
    let { lastMonth } = anchorResult;
    let { lastYear } = anchorResult;

    for (let i = 1; i <= numSamples; i += 1) {
        const result = getExpandedTimestampLabelAndState({
            millisecondsEpoch: ANCHOR_EPOCH_MS + i * stepMs,
            tickInterval,
            locale,
            idx: i,
            currentLastDay: lastDay,
            currentLastMonth: lastMonth,
            currentLastYear: lastYear,
            xAxisMaxLabelParts,
        });
        lastDay = result.lastDay;
        lastMonth = result.lastMonth;
        lastYear = result.lastYear;
        const w = measureWidest(result.label);
        if (w > maxWidth) maxWidth = w;
    }

    ctx.font = prevFont;
    return maxWidth;
};

/**
 * Applies expanded time series config to x-axis
 */
const applyExpandedTimeSeriesConfig = (
    config: uPlot.Axis,
    timeSeriesExpanded: boolean,
    dataLength: number,
    locale: LocaleInfo,
    xAxisMaxLabelParts: number
): uPlot.Axis => {
    if (!timeSeriesExpanded) return config;

    // Pre-compute typical label widths for all tick interval buckets NOW (at config creation
    // time, when all modules are fully initialized). The space callback must not call into
    // utils.ts because it fires during uPlot init before the circular dependency
    // (utils.ts → uplot-utils.ts → utils.ts) is resolved in the test environment.
    //
    // We use a temporary off-screen canvas for measurement so ctx.font is under our control.
    const ALL_TICK_INTERVALS: TickInterval[] = ['1s', '1m', '30m', '1h', '12h', '1d', '7d', '1mo'];
    const FALLBACK_SPACE = 55;
    // Gap added to label width to get minimum tick spacing.
    // Day/week/month labels (e.g. "Sat, Nov 9", "September") are wider and need more gap
    // to prevent overlap. Sub-day labels (e.g. "2:13 PM") are narrower and need less.
    const LABEL_GAP_BY_INTERVAL: Record<TickInterval, number> = {
        '1s': 16,
        '1m': 8,
        '30m': 2,
        '1h': 2,
        '12h': 2,
        '1d': 16,
        '7d': 16,
        '1mo': 16,
    };
    // ms thresholds for interval buckets
    // When the scale range is small enough that ticks have sub-minute spacing,
    // labels include seconds (e.g. "1:08:30 PM") and are wider. Use the actual
    // tick increment to decide: if fullDim is large enough to fit sub-minute ticks,
    // use the wider '1s' width. The threshold accounts for uPlot adding ~10-20%
    // scale padding (e.g. 5min data → ~6min scale range).
    const MS_5MIN = 300000;
    const MS_1H = 3600000;
    const MS_5H = 18000000;
    const MS_12H = 43200000;
    const MS_1D = 86400000;
    const MS_1W = 604800000;
    const MS_1MO = 2592000000;

    const labelWidthByInterval: Partial<Record<TickInterval, number>> = {};
    try {
        const measureCanvas = document.createElement('canvas');
        const measureCtx = measureCanvas.getContext('2d');
        if (measureCtx) {
            const dpr = window.devicePixelRatio || 1;
            const scaledFont = parseAndScaleFont(config.font as string, dpr);
            measureCtx.font = scaledFont;
            for (const interval of ALL_TICK_INTERVALS) {
                const widthPx = getWorstCaseExpandedLabelWidth(
                    measureCtx,
                    interval,
                    locale,
                    xAxisMaxLabelParts,
                    scaledFont
                );
                labelWidthByInterval[interval] = widthPx / dpr;
            }
        }
    } catch {
        // If canvas is unavailable (e.g. SSR), fall back to constant spacing
    }

    // uPlot space callback signature: (self, axisIdx, scaleMin, scaleMax, fullDim)
    // fullDim is the plot width in CSS pixels; scaleMin/scaleMax are the axis scale bounds.
    const space = (_self: uPlot, _axisIdx: number, scaleMin: number, scaleMax: number): number => {
        const rangeMs = (scaleMax - scaleMin) * SECOND_TO_MILLISECOND;
        let tickInterval: TickInterval;
        // Use '1s' bucket for ranges < 5min: ticks will have sub-minute spacing,
        // so labels include seconds (e.g. "1:08:30 PM") and are wider than minute-only labels.
        if (rangeMs < MS_5MIN) tickInterval = '1s';
        else if (rangeMs < MS_1H) tickInterval = '1m';
        else if (rangeMs < MS_5H) tickInterval = '30m';
        else if (rangeMs < MS_12H) tickInterval = '1h';
        else if (rangeMs < MS_1D) tickInterval = '12h';
        else if (rangeMs < MS_1W) tickInterval = '1d';
        else if (rangeMs < MS_1MO) tickInterval = '7d';
        else tickInterval = '1mo';
        const labelWidthCssPx = labelWidthByInterval[tickInterval] ?? FALLBACK_SPACE;
        const gapPx = LABEL_GAP_BY_INTERVAL[tickInterval] ?? 8;
        return labelWidthCssPx + gapPx;
    };

    // Augment uPlot's default time incrs with finer sub-5-minute steps that it omits.
    // Without these, uPlot jumps from 1m → 5m, causing a cliff-edge where a chart
    // that fits 5 ticks at 1m incr suddenly drops to 1 tick at 5m incr.
    const s = 1;
    const m = 60;
    const h = 3600;
    const d = 86400;
    const mo = d * 30;
    const y = d * 365;
    const augmentedTimeIncrs = [
        // sub-second
        0.001,
        0.002,
        0.005,
        0.01,
        0.02,
        0.05,
        0.1,
        0.2,
        0.5,
        // seconds
        s,
        s * 5,
        s * 10,
        s * 15,
        s * 30,
        // minutes — fill the gap uPlot leaves between 1m and 5m
        m,
        m * 2,
        m * 3,
        m * 5,
        m * 10,
        m * 15,
        m * 30,
        // hours
        h,
        h * 2,
        h * 3,
        h * 4,
        h * 6,
        h * 8,
        h * 12,
        // days
        d,
        d * 2,
        d * 3,
        d * 4,
        d * 5,
        d * 6,
        d * 7,
        d * 8,
        d * 9,
        d * 10,
        d * 15,
        // months
        mo,
        mo * 2,
        mo * 3,
        mo * 4,
        mo * 6,
        // years — fine-grained to avoid cliff from 1y → 10y
        y,
        y * 2,
        y * 3,
        y * 4,
        y * 5,
        y * 10,
        y * 25,
        y * 50,
        y * 100,
    ];

    const updatedConfig = { ...config, space, incrs: augmentedTimeIncrs, labelSize: 50 };
    if (dataLength !== 1) {
        updatedConfig.align = 1;
    }
    return updatedConfig;
};

/**
 * Applies custom x-axis label and rotation config
 */
const applyXAxisLabelConfig = (
    config: uPlot.Axis,
    scaleConfig: ScaleConfig,
    timeSeriesExpanded: boolean
): uPlot.Axis => {
    if (!isXScaleConfig(scaleConfig)) return config;

    const customXAxesConfig = scaleConfig.x;
    const xAxisLabel = customXAxesConfig?.label?.length ? customXAxesConfig.label : '';
    const hasAxisLabel = xAxisLabel.length > 0;
    const labelRotation = customXAxesConfig?.labelRotation ?? 0;
    const absRotation = Math.abs(labelRotation);
    const hideLabels = customXAxesConfig?.hideLabels ?? false;

    if (absRotation > 0) {
        return {
            ...config,
            label: customXAxesConfig.label,
            size: calculateRotatedLabelSize(absRotation, timeSeriesExpanded, hasAxisLabel, hideLabels),
            gap: calculateRotatedTickGap(absRotation),
            labelGap: calculateRotatedLabelGap(absRotation),
            rotate: -labelRotation,
            align: getRotationAlignment(labelRotation),
        };
    }

    let axisSize = 0;
    let { labelGap } = config;

    // If labels are hidden, only allocate space for title (if present)
    if (hideLabels) {
        axisSize = hasAxisLabel ? 25 : 0;
    } else if (hasAxisLabel) {
        // Reduced size for tighter spacing around X-axis title
        // 8px (bottom padding) + 12px (labelGap) + ~17px (title height) = ~37px
        axisSize = timeSeriesExpanded ? 34 : 25;
        // Set labelGap to prevent collision between labels and title
        labelGap = 16;
    }

    return {
        ...config,
        label: customXAxesConfig.label,
        size: axisSize,
        labelGap,
    };
};

/**
 * Creates a splits function for Y-axis with a fixed interval
 * @param interval - The interval between tick marks
 * @returns A splits function compatible with uPlot, or undefined if interval is invalid
 */
const createIntervalSplits = (interval: number): uPlot.Axis.Splits | undefined => {
    // Guard: interval must be positive
    if (interval <= 0) {
        return undefined;
    }

    // Minimum pixels needed per label to avoid overlap (font size + padding)
    const MIN_PIXELS_PER_LABEL = 25;

    return (u: uPlot, _axisIdx: number, scaleMin: number, scaleMax: number): number[] => {
        const range = scaleMax - scaleMin;

        // Calculate max splits dynamically based on chart height
        // u.bbox.height is the plot area height in CSS pixels
        const plotHeight = u.bbox?.height ?? 300;
        const maxSplits = Math.max(2, Math.floor(plotHeight / MIN_PIXELS_PER_LABEL));

        // Calculate effective interval - if user's interval would create too many splits,
        // scale it up to a multiple that produces a reasonable number
        let effectiveInterval = interval;
        if (range / interval > maxSplits) {
            const multiplier = Math.ceil(range / interval / maxSplits);
            effectiveInterval = interval * multiplier;
        }

        const splits: number[] = [];
        // Start from a value that aligns with the interval
        // Use floor to include ticks at or below the minimum value
        const start = Math.floor(scaleMin / effectiveInterval) * effectiveInterval;
        for (let val = start; val <= scaleMax && splits.length < maxSplits; val += effectiveInterval) {
            splits.push(val);
        }
        return splits;
    };
};

export const mapScalesToAxes = ({
    xAxisResolution,
    xAxisMaxLabelParts = 3,
    theme,
    locale,
    hideXAxis,
    range,
    isTimeSeries = true,
    scaleConfig = {},
    timestampFormat = 'compact',
    dataLength,
    showXMajorGridLines = false,
    showYMajorGridLines = true,
    showY2MajorGridLines = false,
    showXMajorTicks = true,
    xAxisMajorTickSize,
    yAxisMajorTickInterval,
    y2AxisMajorTickInterval,
}: MapScalesToAxesParams): uPlot.Axis[] => {
    const showOnlyRangeValues = isShowOnlyRangeValues(range);

    // Shared flag: set by the values() callback and read by ticks.filter so that the
    // tick mark is hidden whenever its label was also hidden (no space at right edge).
    const lastTickState = { hide: false };

    // Handle x-axis tick visibility and size
    // If size is explicitly set to 0, hide the ticks
    const xTicksShow = xAxisMajorTickSize === 0 ? false : showXMajorTicks;
    const xTicksConfig: uPlot.Axis['ticks'] = {
        show: xTicksShow,
        width: 1,
        stroke: theme.axis.gridStroke,
        filter: (_filterSelf: uPlot, splits: number[]) => {
            return splits.map((v, idx) => {
                if (idx === splits.length - 1 && lastTickState.hide) {
                    return null;
                }
                return v;
            });
        },
    };
    // Only set size if it's a positive number (not 0, not undefined)
    if (xAxisMajorTickSize !== undefined && xAxisMajorTickSize > 0) {
        xTicksConfig.size = xAxisMajorTickSize;
    }

    let xAxesScaleConfig: uPlot.Axis = {
        show: !hideXAxis,
        font: theme.font,
        stroke: theme.axis.xLabelStroke,
        labelFont: theme.axis.titleFont,
        labelGap: 12,
        grid: {
            show: showXMajorGridLines,
            stroke: theme.axis.gridStroke,
            width: 1,
        },
        ticks: xTicksConfig,
        incrs: !isTimeSeries ? [1] : undefined,
        values: (self: uPlot, splits: number[]): (string | null)[] => {
            // Check if x scale config has hideLabels enabled
            const hideXAxisLabels = scaleConfig.x?.hideLabels;
            const labelRotation = scaleConfig.x?.labelRotation ?? 0;
            const needsCustomRotatedRender = Math.abs(labelRotation) > 0 && timestampFormat === 'expanded';

            if (hideXAxisLabels) {
                // Clear custom rotated labels if they exist
                if (needsCustomRotatedRender) {
                    setRotatedLabels([]);
                }
                // Reset so ticks.filter doesn't consume stale state from a previous render
                lastTickState.hide = false;
                return splits.map(() => '');
            }

            const xRange = splits.at(-1) - splits[0];

            // Reset last tick visibility — will be re-evaluated for current dimensions below.
            // This prevents stale hide=true from a previous (smaller) render persisting on resize.
            lastTickState.hide = false;

            // format tick labels
            let lastDay = '';
            let lastYear = '';
            let lastMonth = '';
            let tickInterval: TickInterval;
            if (splits.length >= 2) {
                tickInterval = getTickInterval((splits[1] - splits[0]) * 1000);
            } else {
                // Single tick — derive interval from the scale range so sub-day
                // data still shows the time part in the label.
                const scaleRange = self?.scales?.x;
                const rangeMs = scaleRange ? (scaleRange.max - scaleRange.min) * 1000 : 0;
                tickInterval = getTickInterval(rangeMs);
            }

            // For 90-degree rotation with expanded format, collect labels for custom rendering
            const customLabels: CustomRotatedLabel[] = [];

            const result = splits.map((v, idx) => {
                if (!isTimeSeries) {
                    // For categorical data, labels are custom-rendered by renderCategoricalLabels
                    // Return empty strings here to avoid double rendering
                    return '';
                }
                const millisecondsEpoch = v * SECOND_TO_MILLISECOND;
                // if expanded timestamp format (multi-line)
                if (timestampFormat === 'expanded') {
                    const expandedResult = getExpandedTimestampLabelAndState({
                        millisecondsEpoch,
                        tickInterval,
                        locale,
                        idx,
                        currentLastDay: lastDay, // Pass the current state
                        currentLastMonth: lastMonth,
                        currentLastYear: lastYear,
                        xAxisMaxLabelParts,
                    });

                    lastDay = expandedResult.lastDay;
                    lastMonth = expandedResult.lastMonth;
                    lastYear = expandedResult.lastYear;

                    // For the last tick in expanded format, only render if there is enough
                    // space so the label does not overflow the right edge of the plot area.
                    // Also update the shared flag so ticks.filter hides the tick mark in sync.
                    // Skip when there is only one tick — hiding the sole tick leaves the
                    // axis with no temporal context at all.
                    if (idx === splits.length - 1 && splits.length > 1) {
                        if (!hasSpaceForLastTick(self, v, expandedResult.label)) {
                            lastTickState.hide = true;
                            return '';
                        }
                        lastTickState.hide = false;
                    }

                    if (needsCustomRotatedRender) {
                        customLabels.push({ split: v, label: expandedResult.label });
                        return '';
                    }

                    return expandedResult.label;
                }

                if (timestampFormat === 'compact') {
                    return formatCompactTimestamp(millisecondsEpoch, xRange, showOnlyRangeValues, locale);
                }
                return null;
            });

            if (needsCustomRotatedRender) {
                setRotatedLabels(customLabels);
            }

            return result;
        },
        splits: createXAxisSplits(showOnlyRangeValues, range, dataLength, isTimeSeries),
    };
    const timeSeriesExpanded = isTimeSeriesWithExpandedTimeFormat(isTimeSeries, timestampFormat);

    xAxesScaleConfig = applyExpandedTimeSeriesConfig(
        xAxesScaleConfig,
        timeSeriesExpanded,
        dataLength,
        locale,
        xAxisMaxLabelParts
    );
    xAxesScaleConfig = applyXAxisLabelConfig(xAxesScaleConfig, scaleConfig, timeSeriesExpanded);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { x, ...yScaleConfigs } = scaleConfig;

    const scaleConfigs = [
        xAxesScaleConfig,
        // default Y axis when no config at all
        ...(Object.keys(yScaleConfigs).length === 0
            ? [
                  {
                      font: theme.font,
                      stroke: theme.axis.yLabelStroke,
                      labelFont: theme.axis.titleFont,
                      gap: 12,
                      labelGap: 0,
                      size: 55,
                      grid: {
                          show: showYMajorGridLines,
                          stroke: theme.axis.gridStroke,
                          width: 1,
                      },
                      ticks: {
                          show: false,
                      },
                      ...(yAxisMajorTickInterval && {
                          splits: createIntervalSplits(yAxisMajorTickInterval),
                      }),
                  },
              ]
            : []),
        // custom Y axes
        ...Object.keys(yScaleConfigs).map<uPlot.Axis>(key => {
            const isRightSide = yScaleConfigs[key].side === 'right';
            // Priority: explicit scaleConfig.showGrid > top-level showYMajorGridLines/showY2MajorGridLines
            const defaultShowGrid = isRightSide ? showY2MajorGridLines : showYMajorGridLines;
            const showGrid = exists(yScaleConfigs[key].showGrid)
                ? yScaleConfigs[key].showGrid
                : defaultShowGrid;

            // Determine tick interval based on axis side
            const tickInterval = isRightSide ? y2AxisMajorTickInterval : yAxisMajorTickInterval;

            // Calculate axis size dynamically based on label presence and explicit width
            const hasLabel = yScaleConfigs[key].label && yScaleConfigs[key].label.length > 0;
            const hasExplicitWidth = exists(yScaleConfigs[key].width);
            let axisSize:
                | number
                | ((u: uPlot, values: string[], axisIdx: number, cycleNum: number) => number)
                | undefined = yScaleConfigs[key].width;

            // If explicit width is set, it means abbreviation is ON (use fixed sizing from config)
            // If no explicit width, abbreviation is OFF (use dynamic sizing)
            if (!hasExplicitWidth) {
                // Abbreviation is OFF - use dynamic sizing function
                // Full values (e.g., "10,000,000") - uPlot will call this function with actual tick label values
                axisSize = (_u, values) => {
                    if (!values || values.length === 0) {
                        return 55; // Default fallback
                    }
                    // Find the longest label
                    const maxLength = Math.max(...values.map(v => (v ? v.length : 0)));
                    // Estimate: ~5.5 pixels per character + base padding
                    const baseWidth = maxLength * 5.5 + 7;
                    // For Y2 (right-side) axes with labels, add space for title
                    // Y2 needs more space than Y-axis due to rotated title positioning
                    if (isRightSide) {
                        return hasLabel ? baseWidth + 15 : baseWidth;
                    }
                    return hasLabel ? baseWidth + 7 : baseWidth;
                };
            }

            // Set labelGap based on whether abbreviation is on/off and has label
            // Use hasExplicitWidth to determine abbreviation (width set = abbreviated, no width = not abbreviated)
            // For Y2 (right-side) axes, use tighter spacing since title positioning differs
            // Abbreviated labels (hasExplicitWidth): Y-axis uses 5px, Y2-axis uses 3px
            // Non-abbreviated labels (!hasExplicitWidth): both use 1px for tight spacing
            let labelGap = 0;
            if (hasLabel) {
                if (hasExplicitWidth) {
                    // Abbreviation ON - use tighter gap
                    labelGap = isRightSide ? 3 : 5;
                } else {
                    // Abbreviation OFF - use minimal gap
                    labelGap = 1;
                }
            }

            return {
                scale: key,
                label: yScaleConfigs[key].label,
                labelFont: theme.axis.titleFont,
                font: theme.font,
                gap: 12,
                labelGap,
                stroke: theme.axis.yLabelStroke,
                side: isRightSide ? 1 : 3,
                ...(axisSize !== undefined && { size: axisSize }),
                grid: {
                    show: showGrid,
                    stroke: theme.axis.gridStroke,
                    width: 1,
                },
                ticks: {
                    show: false,
                },
                ...(tickInterval && tickInterval > 0 && { splits: createIntervalSplits(tickInterval) }),
                values: getYAxisValuesConfig(yScaleConfigs[key], xAxisResolution),
            };
        }),
    ];

    return scaleConfigs;
};

type StackGroup = {
    series: number[]; // series index
    acc: number[];
    dir: -1 | 1;
};

export type SeriesWithOptionalStackingGroup = SeriesWithKey & {
    negY?: boolean;
    stacking?: {
        mode: 'none' | 'normal' | 'percent';
        group: string;
    };
};

// Helper function to apply negY transformation
const applyNegYTransform = (vals: (number | null)[], negY: boolean): (number | null)[] => {
    if (!negY) return vals;

    return vals.map(val => {
        const value = val || 0;
        return value * -1;
    });
};

// Helper function to create or get stacking group
const getOrCreateStackGroup = (
    stackKey: string,
    stackGroups: Map<string, StackGroup>,
    zeroArr: number[],
    hasPos: boolean
): StackGroup => {
    let group = stackGroups.get(stackKey);

    if (!exists(group)) {
        group = {
            series: [],
            acc: zeroArr.slice(),
            dir: hasPos ? -1 : 1,
        };
        stackGroups.set(stackKey, group);
    }

    return group;
};

// Helper function to process stacking for a series
const processStacking = (
    s: SeriesWithOptionalStackingGroup,
    si: number,
    vals: (number | null)[],
    dataLen: number,
    stackGroups: Map<string, StackGroup>,
    seriesStackKeys: string[],
    data: (number | null)[][]
): { seriesStackKeys: string[]; data: (number | null)[][] } => {
    const hasPos = vals.some((v: number | null | undefined) => (v || 0) > 0);
    const { stacking } = s;
    if (!stacking) return { seriesStackKeys, data };

    const stackKey = stacking.mode + stacking.group + (hasPos ? '+' : '-');
    const newSeriesStackKeys = [...seriesStackKeys];
    newSeriesStackKeys[si] = stackKey;

    const group = getOrCreateStackGroup(stackKey, stackGroups, Array(dataLen).fill(0), hasPos);
    group.series.unshift(si);

    const newData = [...data];
    newData[si] = Array(dataLen);
    const stacked = newData[si];
    const { acc } = group;

    for (let i = 0; i < dataLen; i += 1) {
        const v = vals[i];
        if (v != null) {
            acc[i] += v;
            stacked[i] = acc[i];
        } else {
            stacked[i] = v;
        }
    }

    return { seriesStackKeys: newSeriesStackKeys, data: newData };
};

// Helper function to process percent stacking
const processPercentStacking = (
    s: SeriesWithOptionalStackingGroup,
    si: number,
    dataLen: number,
    stackGroups: Map<string, StackGroup>,
    seriesStackKeys: string[],
    data: (number | null)[][]
): void => {
    if (s.stacking?.mode !== 'percent') return;

    const group = stackGroups.get(seriesStackKeys[si]);
    if (!exists(group)) return;

    const sign = group.dir * -1;
    const stacked = data[si];

    for (let i = 0; i < dataLen; i += 1) {
        const v = stacked[i];
        if (v != null) {
            stacked[i] = sign * (v / group.acc[i]);
        }
    }
};

// Helper function to generate bands
const generateBands = (stackGroups: Map<string, StackGroup>): uPlot.Band[] => {
    const bands: uPlot.Band[] = [];

    stackGroups.forEach(group => {
        const { series: groupSeries, dir } = group;
        const lastIdx = groupSeries.length - 1;

        groupSeries.forEach((si, i) => {
            if (i !== lastIdx) {
                const nextIdx = groupSeries[i + 1];
                bands.push({
                    series: [si, nextIdx],
                    dir,
                });
            }
        });
    });

    return bands;
};

/**
 * Stacks the data according to the series properties. Returns the transformed
 * stacked data to be used in the plot, and the band configurations that should
 * be provided to uPlot.
 *
 * Derived from: https://github.com/leeoniya/uPlot/blob/24663e48a0db7247692dbf61280174fa71b17980/demos/stack.js
 */
export function stackData(
    series: SeriesWithOptionalStackingGroup[],
    _data: uPlot.AlignedData
): { data: uPlot.AlignedData; bands?: uPlot.Band[] } {
    // If no series to stack, just return existing data
    if (!series.some(s => exists(s.stacking))) {
        return { data: _data, bands: undefined };
    }

    const data = Array(series.length);
    const dataLen = _data[0]?.length || 0;
    const stackGroups: Map<string, StackGroup> = new Map();
    const seriesStackKeys = Array(series.length);

    // Compute the totals for each group
    let currentSeriesStackKeys = seriesStackKeys;
    let currentData = data;

    // Process non-stacked series first (forward order)
    series.forEach((s, si) => {
        if (!s.stacking || s.stacking.mode === 'none') {
            const originalVals = Array.from(_data[si]);
            const vals = applyNegYTransform(originalVals, s.negY || false);
            currentData[si] = vals;
        }
    });

    // Process stacked series in reverse order to match Highcharts reversedStacks:true behavior.
    // This puts the first series at the top and the last series at the bottom, matching
    // the Highcharts default where the first series in the legend stacks on top.
    for (let si = series.length - 1; si >= 0; si -= 1) {
        const s = series[si];
        if (s.stacking && s.stacking.mode !== 'none') {
            const originalVals = Array.from(_data[si]);
            const vals = applyNegYTransform(originalVals, s.negY || false);
            const result = processStacking(
                s,
                si,
                vals,
                dataLen,
                stackGroups,
                currentSeriesStackKeys,
                currentData
            );
            currentSeriesStackKeys = result.seriesStackKeys;
            currentData = result.data;
        }
    }

    // Re-compute by percent if necessary
    series.forEach((s, si) => {
        processPercentStacking(s, si, dataLen, stackGroups, currentSeriesStackKeys, currentData);
    });

    const bands = generateBands(stackGroups);

    return {
        data: currentData,
        bands,
    };
}

export function getSeriesKey(seriesIndex?: number, allSeries?: SeriesInfo[]) {
    return typeof seriesIndex === 'number' && seriesIndex < allSeries?.length
        ? allSeries[seriesIndex].field
        : undefined;
}

export type AxisPaddingInfo = {
    hasYAxisLabel: boolean;
    hasAbbreviationOff: boolean;
    hasY2Config: boolean;
    hasYAxisWithData: boolean;
    hasY2AxisLabel: boolean;
    hasY2AbbreviationOff: boolean;
    hasY2AxisWithData: boolean;
};

export const getScaleKeyFlags = (key: string, config: ScaleConfig[string]): Partial<AxisPaddingInfo> => {
    if (key === 'y2' || config.side === 'right') {
        return {
            hasY2Config: true,
            hasY2AxisLabel: config.label && config.label.length > 0,
            hasY2AbbreviationOff: !config.width,
        };
    }
    if (key === 'y') {
        return {
            hasYAxisLabel: config.label && config.label.length > 0,
            hasAbbreviationOff: !config.tickFmt,
        };
    }
    return {};
};

export const getSeriesScaleFlags = (
    sc: SeriesConfig[string],
    hasY2Config: boolean
): Partial<AxisPaddingInfo> => {
    const scaleValue = sc?.scale || sc?.overrides?.scale;
    if (scaleValue === 'y2') return { hasY2AxisWithData: true };
    if (scaleValue === 'y' || (!hasY2Config && (scaleValue === undefined || scaleValue === null))) {
        return { hasYAxisWithData: true };
    }
    return {};
};

export const getAxisPaddingInfo = (
    scaleConfig: ScaleConfig | undefined,
    seriesConfig: SeriesConfig | undefined
): AxisPaddingInfo => {
    let info: AxisPaddingInfo = {
        hasYAxisLabel: false,
        hasAbbreviationOff: false,
        hasY2Config: false,
        hasYAxisWithData: false,
        hasY2AxisLabel: false,
        hasY2AbbreviationOff: false,
        hasY2AxisWithData: false,
    };

    if (scaleConfig) {
        Object.keys(scaleConfig).forEach(key => {
            if (key !== 'x' && scaleConfig?.[key]) {
                info = { ...info, ...getScaleKeyFlags(key, scaleConfig[key]) };
            }
        });
    }

    if (seriesConfig) {
        Object.values(seriesConfig).forEach(sc => {
            info = { ...info, ...getSeriesScaleFlags(sc, info.hasY2Config) };
        });
    }

    return info;
};

const calculateLeftPadding = (
    hasY2Config: boolean,
    hasYAxisWithData: boolean,
    hasYAxisLabel: boolean,
    hasAbbreviationOff: boolean
): number => {
    if (hasY2Config && !hasYAxisWithData) return 30;
    if (!hasYAxisWithData) return 10;
    if (hasYAxisLabel) {
        return hasAbbreviationOff ? -12 : -4;
    }
    return hasAbbreviationOff ? -18 : -1;
};

const calculateRightPadding = (
    hasY2Config: boolean,
    hasY2AxisWithData: boolean,
    hasY2AxisLabel: boolean,
    hasY2AbbreviationOff: boolean
): number => {
    if (!hasY2Config || !hasY2AxisWithData) return 10;
    if (hasY2AxisLabel) {
        return hasY2AbbreviationOff ? -5 : -3;
    }
    return hasY2AbbreviationOff ? 5 : 9;
};

export const calculateExpandedPadding = (
    scaleConfig: ScaleConfig | undefined,
    seriesConfig: SeriesConfig | undefined
): [number, number, number, number] => {
    const {
        hasYAxisLabel,
        hasAbbreviationOff,
        hasY2Config,
        hasYAxisWithData,
        hasY2AxisLabel,
        hasY2AbbreviationOff,
        hasY2AxisWithData,
    } = getAxisPaddingInfo(scaleConfig, seriesConfig);

    const leftPadding = calculateLeftPadding(
        hasY2Config,
        hasYAxisWithData,
        hasYAxisLabel,
        hasAbbreviationOff
    );
    const rightPadding = calculateRightPadding(
        hasY2Config,
        hasY2AxisWithData,
        hasY2AxisLabel,
        hasY2AbbreviationOff
    );

    return [10, rightPadding, 5, leftPadding];
};

export const getLocaleInfo = (userLocale: LocaleInfo | undefined, defaultLocale: LocaleInfo): LocaleInfo =>
    userLocale && Object.keys(userLocale).length > 0 ? userLocale : defaultLocale;

export const getIsSingleVizFullscreen = (): boolean => {
    const fullscreenEl = typeof document !== 'undefined' ? document.fullscreenElement : null;
    return !!fullscreenEl && fullscreenEl.tagName !== 'BODY';
};
